// OfflineBanner — pasek stanu sieci i kolejki na górze ekranu. Lampki TYLKO od tego, co nie działa.
//
// Stany (V3 słaby zasięg B-1 + UZUPEŁNIENIE 2 i DN1 = (b), decyzje Martina 12.09.2026):
//   Tryb lokalny (B9)         → stały żółty baner: "🔒 Tryb lokalny: N zdjęć na telefonie (X MB)"
//                               + przełącznik + „Wyślij teraz"; toast „Masz zasięg. Włączyć
//                               synchronizację?" z JEDNYM przyciskiem przy `online` / powrocie
//                               do aplikacji (bez automatu)
//   Offline + kolejka > 0     → żółty: "📵 Offline — zdjęcia zapisane lokalnie (N w kolejce)";
//                               offline z pustą kolejką = NIC do komunikowania (aneks 2)
//   Słaby zasięg (B3)         → "📶 Słaby zasięg, pokazuję ostatni stan" (GET przekroczył 6 s, cache)
//   Kolejka — WYŁĄCZNIE wyjątki (queueHealth.evaluateQueueBanner):
//     (a) ZATOR   → "⚠ Kolejka stoi: N zdjęć, ostatnie wysłane X min temu" + „Wyślij teraz"
//     (b) FAILED  → "⚠ N zdjęć nie udało się wysłać" + „Ponów"
//     (c) PRÓG    → "⏳ N zdjęć (X MB) czeka na wysłanie" (informacyjny, > 30 zdjęć / > 100 MB)
//   Schodząca kolejka         → CISZA (⏳ w slotach zostaje; stan w stopce QueueFooter)
//   Kolejka > 500 MB (B12f)   → dodatkowy pasek ostrzeżenia; NIGDY blokada aparatu.
//   Odrzucenie w tle (B12a)   → toast z powodem (duplikat / za mała rozdzielczość).
//
// DN1 = (b): przełącznik „Tryb lokalny" WYŁĄCZNIE w banerze (gdy baner jest widoczny: kolejka /
// offline / słaby zasięg) oraz w Serwisie. ZERO stałego wiersza stanu sieci na ekranach —
// bezpiecznik awaryjny ma nie kusić do przełączania bez potrzeby.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useOffline } from '../../hooks/useOffline.js';
import { useAuth } from '../../hooks/useAuth.js';
import { uploadManager } from '../../services/uploadManager.js';
import { isLocalMode, setLocalMode, subscribeLocalMode } from '../../services/localMode.js';
import { getNetState, subscribeNetState, WEAK_SIGNAL_TTL_MS } from '../../services/netActivity.js';
import {
  evaluateQueueBanner, getLastSuccessAt, subscribeQueueHealth, fmtAgo, plZdjec, HEALTH_TICK_MS,
} from '../../services/queueHealth.js';
import { showToast } from '../../shared/toastBus.js';
import { COLORS, FONTS } from '../../shared/tokens.js';
import ForceUploadButton from './ForceUploadButton.jsx';
// MONIT 2026-10-02_0526: język UI.
import { useLang } from '../../hooks/useLang.js';
import { t } from '../../shared/i18n.js';

export const QUEUE_WARN_BYTES = 500 * 1024 * 1024;
const REACH_TOAST_MIN_GAP_MS = 5 * 60 * 1000;

export function fmtMb(bytes) {
  const mb = (Number(bytes) || 0) / (1024 * 1024);
  return mb >= 100 ? `${Math.round(mb)} MB` : `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

const RED_BG = 'rgba(var(--fo-red-rgb, 239,68,68), 0.14)';
const RED_BORDER = 'var(--fo-red, #ef4444)';

export default function OfflineBanner() {
  const { isOnline, queueCount, queueStats } = useOffline();
  // GATE'Y SUPPORT (monit 1215 zad.3): role serwisowe nie widzą „Wyślij teraz"
  // w globalnym banerze (19.08: klik supporta = seria 403 + nadpisane lastError).
  const { isServiceRole, isLoggedIn } = useAuth();
  useLang();

  // V3 B9: Tryb lokalny (flaga per urządzenie z IDB, przez localMode.js).
  const [localMode, setLocalModeState] = useState(isLocalMode());
  useEffect(() => subscribeLocalMode(setLocalModeState), []);
  const toggleLocalMode = useCallback(async (next) => {
    const v = await setLocalMode(next);
    if (v) {
      showToast(t('ob.localOn'), { kind: 'warn', ms: 3500 });
    } else {
      showToast(t('ob.syncOn'), { kind: 'ok', ms: 2500 });
      uploadManager.processQueue().catch(() => {});
    }
  }, []);

  // V3 B9: „Masz zasięg. Włączyć synchronizację?" — przy `online` i powrocie do apki,
  // gdy Tryb lokalny włączony i przeglądarka zgłasza online. Jeden przycisk, bez automatu,
  // nie częściej niż co 5 min.
  const [reachToast, setReachToast] = useState(false);
  const lastReachRef = useRef(0);
  useEffect(() => {
    const maybeShow = () => {
      if (!isLocalMode()) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (Date.now() - lastReachRef.current < REACH_TOAST_MIN_GAP_MS) return;
      lastReachRef.current = Date.now();
      setReachToast(true);
    };
    const onVis = () => { if (document.visibilityState === 'visible') maybeShow(); };
    window.addEventListener('online', maybeShow);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('online', maybeShow);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
  useEffect(() => { if (!localMode) setReachToast(false); }, [localMode]);

  // V3 B3: sygnał „słaby zasięg" z useApi (timeout GET-u → cache).
  const [weak, setWeak] = useState(() => getNetState().weak);
  useEffect(() => {
    const unsub = subscribeNetState((ev) => setWeak(ev.type === 'weak'));
    return unsub;
  }, []);
  useEffect(() => {
    if (!weak) return undefined;
    const t = setTimeout(() => setWeak(getNetState().weak), WEAK_SIGNAL_TTL_MS + 100);
    return () => clearTimeout(t);
  }, [weak]);

  // UZUPEŁNIENIE 2: ostatni SUKCES wysyłki (zator liczony od niego) + tykanie co 30 s,
  // żeby zator ujawnił się bez żadnego zdarzenia kolejki (nic nie leci = nic nie emituje).
  const [lastSuccessAt, setLastSuccessAt] = useState(() => getLastSuccessAt());
  useEffect(() => subscribeQueueHealth(setLastSuccessAt), []);
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), HEALTH_TICK_MS);
    return () => clearInterval(t);
  }, []);

  // R2b-b: toast po skanie martwych blobów — „zero cichego znikania bez śladu".
  // V3 B12a: toast przy KAŻDYM odrzuceniu w tle (duplikat / miniatura) z powodem.
  const [lostToast, setLostToast] = useState(0);
  useEffect(() => uploadManager.subscribe((ev) => {
    if (ev.type === 'lost-scan-done' && ev.lostCount > 0) {
      setLostToast(ev.lostCount);
    }
    if (ev.type === 'photo-lost' && typeof ev.reason === 'string' && ev.reason.startsWith('odrzucone')) {
      const where = [ev.floor, ev.subfolder, ev.slotType].filter(Boolean).join(' / ');
      showToast(t('ob.photoLost', { where, reason: ev.reason }), { kind: 'error', ms: 9000 });
    }
  }), []);
  useEffect(() => {
    if (!lostToast) return undefined;
    const t = setTimeout(() => setLostToast(0), 12000);
    return () => clearTimeout(t);
  }, [lostToast]);

  const queueBytes = queueStats.bytes || 0;

  // R2b-b: pasek toasta niezależny od stanu kolejki (po skanie kolejka bywa pusta
  // → baner główny znika, a informacja o usuniętych MUSI zostać widoczna).
  const lostStrip = lostToast > 0 ? (
    <div
      role="status"
      style={{
        padding: '8px 12px', fontSize: 13,
        background: RED_BG,
        borderBottom: `1px solid ${RED_BORDER}`,
        position: 'sticky', top: 0, zIndex: 101,
        color: COLORS.text, fontFamily: FONTS.body,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      <span>{t('ob.lostStrip', { n: lostToast })}</span>
    </div>
  ) : null;

  // V3 B12f: ostrzeżenie o pamięci (kolejka > 500 MB). Nigdy blokada aparatu.
  const memoryStrip = queueBytes > QUEUE_WARN_BYTES ? (
    <div role="status" data-memory-warn="1" style={{
      padding: '6px 12px', fontSize: 12,
      background: 'rgba(234,88,12,0.16)', borderBottom: '1px solid rgba(234,88,12,0.6)',
      position: 'sticky', top: 0, zIndex: 101, color: COLORS.text, fontFamily: FONTS.body,
      textAlign: 'center',
    }}>
      {t('ob.memory', { mb: fmtMb(queueBytes) })}
    </div>
  ) : null;

  // V3 B9: toast „Masz zasięg" — jeden przycisk; tap w tekst chowa (bez automatu).
  // Nad ToastHost (zIndex 1500, dół 16 px; trzyma do 3 toastów ~200 px): toast z przyciskiem
  // musi być widoczny także przy stosie zwykłych toastów (zrzut harnessu 12.09: przykryty).
  const reachStrip = reachToast && localMode ? (
    <div role="status" data-reach-toast="1" style={{
      position: 'fixed', left: 12, right: 12,
      bottom: 'calc(230px + env(safe-area-inset-bottom, 0px))', zIndex: 1600,
      maxWidth: 560, margin: '0 auto',
      background: 'rgba(16,16,18,0.97)', color: '#fff',
      border: '1px solid #eab308', borderRadius: 12,
      padding: '11px 14px', fontSize: 13.5, fontWeight: 600, lineHeight: 1.4,
      boxShadow: '0 8px 24px rgba(0,0,0,0.5)', fontFamily: FONTS.body,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span onClick={() => setReachToast(false)} style={{ flex: 1, cursor: 'pointer' }}>
        {t('ob.reachQ')}
      </span>
      <button
        onClick={() => { setReachToast(false); toggleLocalMode(false); }}
        style={{
          background: '#eab308', color: '#111', border: 'none', borderRadius: 8,
          padding: '8px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          flexShrink: 0, fontFamily: FONTS.body, WebkitTapHighlightColor: 'transparent',
        }}
      >
        {t('ob.enableSync')}
      </button>
    </div>
  ) : null;

  // DN1 = (b): przełącznik żyje TYLKO w banerach poniżej (i w Serwisie). Zero stałego wiersza.
  const localToggle = !isServiceRole && isLoggedIn ? (
    <button
      onClick={() => toggleLocalMode(!localMode)}
      role="switch"
      aria-checked={localMode}
      aria-label={localMode ? t('ob.toggleAriaOn') : t('ob.toggleAriaOff')}
      data-local-mode-toggle={localMode ? 'on' : 'off'}
      title={localMode ? t('ob.toggleTitleOn') : t('ob.toggleTitleOff')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 8px', borderRadius: 999,
        background: localMode ? '#eab308' : 'rgba(255,255,255,0.08)',
        color: localMode ? '#111' : COLORS.textMuted,
        border: localMode ? '1px solid #eab308' : '1px solid rgba(255,255,255,0.18)',
        fontSize: 11, fontWeight: 700, fontFamily: FONTS.body,
        cursor: 'pointer', WebkitTapHighlightColor: 'transparent', flexShrink: 0,
        marginLeft: 8,
      }}
    >
      <span aria-hidden="true">{localMode ? '🔒' : '🔓'}</span>
      {t('ob.localMode')}
      <span aria-hidden="true" style={{
        width: 22, height: 12, borderRadius: 6, position: 'relative',
        background: localMode ? '#111' : 'rgba(255,255,255,0.25)',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: localMode ? 12 : 2, width: 8, height: 8,
          borderRadius: '50%', background: localMode ? '#eab308' : '#fff', transition: 'left 0.15s',
        }} />
      </span>
    </button>
  ) : null;

  // V3 B9: Tryb lokalny = stały baner (nie toast) z licznikiem i MB.
  if (localMode) {
    return (
      <>
        {lostStrip}
        {memoryStrip}
        <div
          role="status"
          data-banner="local-mode"
          style={{
            padding: '8px 12px', fontSize: 13,
            background: 'rgba(234,179,8,0.16)', borderBottom: '1px solid #eab308',
            position: 'sticky', top: 0, zIndex: 100,
            color: COLORS.text, fontFamily: FONTS.body,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexWrap: 'wrap',
          }}
        >
          <span style={{ flex: '0 1 auto' }}>
            {t('ob.localBanner', { n: queueCount, photos: plZdjec(queueCount), mb: fmtMb(queueBytes) })}
          </span>
          {localToggle}
          {!isServiceRole && <ForceUploadButton />}
        </div>
        {reachStrip}
      </>
    );
  }

  // Który baner (jeśli w ogóle): offline z kolejką > słaby zasięg > wyjątek kolejki > cisza.
  let kind = null; let message = null; let bg = null; let border = null; let forceLabel = null;
  const health = evaluateQueueBanner({
    online: isOnline, weak, localMode, stats: queueStats, lastSuccessAt, now: Date.now(),
  });
  if (!isOnline) {
    if (queueCount > 0) {
      kind = 'offline';
      message = t('ob.offline', { n: queueCount });
      bg = `${COLORS.warning}20`; border = `${COLORS.warning}80`;
    }
  } else if (weak) {
    // V3 B3: słabe 5G zgłasza „online", GET przekroczył limit → ostatni stan z cache.
    kind = 'weak';
    message = queueCount > 0 ? t('ob.weakN', { n: queueCount }) : t('ob.weak');
    bg = `${COLORS.warning}20`; border = `${COLORS.warning}80`;
  } else if (health?.kind === 'failed') {
    kind = 'failed';
    message = t('ob.failed', { n: health.failed, photos: plZdjec(health.failed), waiting: health.waiting > 0 ? t('ob.waiting', { n: health.waiting }) : '' });
    bg = RED_BG; border = RED_BORDER; forceLabel = t('ob.retry');
  } else if (health?.kind === 'stall') {
    kind = 'stall';
    const ago = health.lastSuccessAt ? fmtAgo(Date.now() - health.lastSuccessAt) : null;
    message = t('ob.stall', { n: health.total, photos: plZdjec(health.total), tail: ago ? t('queue.lastSent', { ago }) : t('queue.nothingSent') });
    bg = RED_BG; border = RED_BORDER; forceLabel = t('force.idle');
  } else if (health?.kind === 'threshold') {
    kind = 'threshold';
    message = t('ob.threshold', { n: health.total, photos: plZdjec(health.total), mb: fmtMb(health.bytes) });
    bg = `${COLORS.warning}20`; border = `${COLORS.warning}80`;
  }

  if (!kind) {
    // Cisza: schodząca kolejka, online, pusta kolejka, offline bez kolejki. Stan w stopce.
    return <>{lostStrip}{memoryStrip}{reachStrip}</>;
  }

  return (
    <>
      {lostStrip}
      {memoryStrip}
      <div
        role="status"
        data-banner={kind}
        style={{
          padding: '8px 12px',
          fontSize: 13,
          background: bg,
          borderBottom: `1px solid ${border}`,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          color: COLORS.text,
          fontFamily: FONTS.body,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ flex: '0 1 auto' }}>{message}</span>
        {localToggle}
        {/* Sprint #2 (2026-05-04 wieczór): manual upload trigger gdy Android Chrome
            opóźnia automatyczny sync. Self-service workaround dla techników.
            GATE'Y SUPPORT (monit 1215): role serwisowe bez przycisku — wysyłka
            w Serwisie wyłącznie dedykowanymi kontrolkami support-plus.
            UZUPEŁNIENIE 2: przycisk tylko przy ZATORZE („Wyślij teraz") i FAILED („Ponów")
            oraz przy słabym zasięgu / offline (jak dotąd); próg = sam komunikat. */}
        {!isServiceRole && kind !== 'threshold' && <ForceUploadButton label={forceLabel || undefined} />}
      </div>
      {reachStrip}
    </>
  );
}
